import { Component, Input, OnInit } from '@angular/core';

import { NEGATIVE_INDICES_COLOR, POSITIVE_INDICES_COLOR } from 'IndicesApp/ts/apps/index-family-app/constants';
import { UserPreference } from 'AccountsApp/ts/interfaces/user-preference.interface';
import {
    GainersLosers,
    IndicesPageGainersLosersGetViewResponse,
} from 'IndicesApp/ts/interfaces/indices-view-responses.interface';

import { AccountsService } from 'AccountsApp/ts/services/accounts.service';
import { IndicesService } from 'IndicesApp/ts/shared/services/indices.service';

import { formatChange } from 'Angular/utils/string-utils';

@Component({
    standalone: false,
    selector: 'ycn-indices-page-gainers-losers',
    templateUrl: './indices-page-gainers-losers.template.html',
})
export class IndicesPageGainersLosersComponent implements OnInit {
    @Input() currFamilyName!: string;

    gainers: GainersLosers[] = [];

    losers: GainersLosers[] = [];

    gainerColors: string[] = POSITIVE_INDICES_COLOR;

    loserColors: string[] = NEGATIVE_INDICES_COLOR;

    gainersNoData: boolean = true;

    losersNoData: boolean = true;

    loading: boolean = true;

    periodicity: string | null = null;

    PERIODICITY_OPTIONS: { label: string; value: string }[] = [
        { label: '1d', value: '1d' },
        { label: '5d', value: '5d' },
        { label: '1m', value: '1m' },
        { label: '3m', value: '3m' },
        { label: '6m', value: '6m' },
        { label: 'YTD', value: 'ytd' },
        { label: '1y', value: '1y' },
        { label: '5y', value: '5y' },
        { label: '10y', value: '10y' },
        { label: 'MAX', value: 'max' },
    ];

    constructor(private accountsService: AccountsService, private indicesService: IndicesService) {}

    ngOnInit(): void {
        this.accountsService.getUserPreferences().subscribe({
            next: (preference: UserPreference) => {
                this.periodicity = preference.indices_page_performance_by_family_periodicity;
                this.getGainersLosers(this.currFamilyName, this.periodicity);
            },
            error: () => {
                this.periodicity = '1y'; // if we don't have a saved preference for periodicity
                this.getGainersLosers(this.currFamilyName, this.periodicity);
            },
        });
    }

    formatChanges(indices: GainersLosers[]): GainersLosers[] {
        /**
         * reformats each index changes so positive and 0 values have leading '+'.
         */
        const gainersLosersWithReformattedChanges: GainersLosers[] = [];
        for (let i = 0; i < indices.length; i += 1) {
            const index = Object.assign(indices[i]);
            index.change_percent = formatChange(index.change_percent);
            gainersLosersWithReformattedChanges.push(index);
        }
        return gainersLosersWithReformattedChanges;
    }

    getGainersLosersColorGradient(indices: GainersLosers[], isGainer: boolean): GainersLosers[] {
        /**
         * Assigns the color gradient to each index. If the `change_percent` is the same as the previous
         * index, the same color is used.
         */
        const colors: string[] = isGainer ? this.gainerColors : this.loserColors;
        let colorIndex = 0;
        const gainersLosersWithColorGradient: GainersLosers[] = [];
        for (let i = 0; i < indices.length; i += 1) {
            // Create the color attribute for each index
            const index = Object.assign(indices[i], { color: '' });
            if (indices[i - 1] && indices[i].change_percent === indices[i - 1].change_percent) {
                index.color = gainersLosersWithColorGradient[i - 1].color;
            } else {
                index.color = colors[colorIndex] ? colors[colorIndex] : 'bg-neutral';
                colorIndex += 1;
            }
            gainersLosersWithColorGradient.push(index);
        }
        return gainersLosersWithColorGradient;
    }

    getGainersLosers(indexFamily: string, periodicity: string): void {
        this.currFamilyName = indexFamily;
        this.periodicity = periodicity;
        this.loading = true;
        this.indicesService.getIndicesPageGainersLosers(this.currFamilyName, this.periodicity).subscribe({
            next: (response: IndicesPageGainersLosersGetViewResponse) => {
                this.gainersNoData = response.gainers.length === 0;
                this.losersNoData = response.losers.length === 0;
                this.gainers = this.formatChanges(this.getGainersLosersColorGradient(response.gainers, true));
                this.losers = this.formatChanges(this.getGainersLosersColorGradient(response.losers, false));
                this.loading = false;
            },
            error: () => {
                this.gainersNoData = true;
                this.losersNoData = true;
                this.loading = false;
            },
        });
    }

    updatePeriodicity(periodicityOptionValue: string): void {
        this.getGainersLosers(this.currFamilyName, periodicityOptionValue);
    }
}
